'use client';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useRef } from 'react';
import { apiRequest } from '~/api';
import { toast } from '~/toast';
import { Button } from '../ui';

export const FormImageUpload = ({
  path,
  label,
  setImageId,
  uploadLabel,
}: {
  path: string;
  label?: string;
  uploadLabel?: string;
  setImageId?: (imageId: string | null) => void;
}) => {
  const uploadPhotoFormRef = useRef<HTMLFormElement>(null);

  const upload = useMutation({
    mutationFn: async (formData: FormData) => {
      const { imageId } = await apiRequest<{
        imageId: string | null;
        error: string | null;
      }>({
        path,
        data: formData,
      });

      if (setImageId) {
        setImageId(imageId);
      }
    },
    onSuccess: async () => {
      toast.success('Updated image');
    },
    onError: () => {
      toast.error('Unable to upload image. Please try again.');
    },
  });

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formElement = uploadPhotoFormRef.current;
    const file = event.target.files?.[0];

    if (!formElement || !file) return;

    const formData = new FormData(formElement);

    upload.mutate(formData);
  };

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4">
      {label && <div>{label}</div>}

      <form
        className="space-y-6 md:space-y-10 lg:space-y-14"
        ref={uploadPhotoFormRef}
        encType="multipart/form-data"
      >
        <div className="relative w-44">
          <Button
            className="pointer-events-none w-full"
            size="md"
            variant="secondary"
          >
            {upload.isPending ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              uploadLabel || 'Update image'
            )}
          </Button>

          <input
            className="absolute left-0 top-0 h-full w-full opacity-0"
            id="photo-upload"
            name="file"
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
          />
        </div>
      </form>
    </div>
  );
};
